import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';

/**
 * The five small "Add X" dialogs used on the Items page.
 * Kept together because they share the same shell and simple field shape.
 */

export const AddVendorDialog = ({ open, onOpenChange, name, setName, saving, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm rounded-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1F2937]">Add New Vendor</DialogTitle>
      </DialogHeader>
      <div className="space-y-2">
        <Label className="text-sm font-medium text-[#1F2937]">Vendor Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter vendor name"
          className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
        <Button onClick={onSave} disabled={saving || !name.trim()} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Add Vendor
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddBrandDialog = ({ open, onOpenChange, name, setName, saving, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm rounded-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1F2937]">Add New Brand</DialogTitle>
      </DialogHeader>
      <div className="space-y-2">
        <Label className="text-sm font-medium text-[#1F2937]">Brand Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter brand name"
          className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
          data-testid="new-brand-name-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onSave(); }}
          autoComplete="off"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
        <Button onClick={onSave} disabled={saving || !name.trim()} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-brand-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Add Brand
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddCategoryDialog = ({ open, onOpenChange, name, setName, code, setCode, saving, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm rounded-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1F2937]">Add New Category</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#1F2937]">Category Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Electrical"
            className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
            data-testid="new-category-name-input"
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#1F2937]">Category Code *</Label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g., ELEC"
            className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD] font-mono"
            data-testid="new-category-code-input"
            autoComplete="off"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
        <Button onClick={onSave} disabled={saving || !name.trim() || !code.trim()} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-category-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Add Category
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddSubCategoryDialog = ({ open, onOpenChange, parentName, name, setName, code, setCode, saving, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm rounded-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1F2937]">Add New Subcategory</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#6B7280]">Parent Category</Label>
          <Input value={parentName} disabled className="h-10 rounded-lg border-gray-200 bg-gray-50 font-medium" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#1F2937]">Subcategory Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Switches"
            className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
            data-testid="new-subcategory-name-input"
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#1F2937]">Subcategory Code *</Label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g., SWT"
            className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD] font-mono"
            data-testid="new-subcategory-code-input"
            autoComplete="off"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
        <Button onClick={onSave} disabled={saving || !name.trim() || !code.trim()} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-subcategory-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Add Subcategory
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddUnitDialog = ({ open, onOpenChange, value, setValue, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm rounded-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1F2937]">Add Custom Unit</DialogTitle>
      </DialogHeader>
      <div className="space-y-2">
        <Label className="text-sm font-medium text-[#1F2937]">Unit Name *</Label>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          placeholder="e.g., SPOOL"
          className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD] font-mono"
          data-testid="new-unit-value-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) onSave(); }}
          autoComplete="off"
        />
        <p className="text-xs text-[#9CA3AF]">Will be converted to uppercase automatically.</p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
        <Button onClick={onSave} disabled={!value.trim()} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-unit-btn">
          Add Unit
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
